import React, { useState } from "react";
import { X, CheckCircle } from "lucide-react";
import businessConfig from "../config/businessConfig";

// ── Content for every clickable footer link ───────────────────
// ⚠️ "Iron Only" renamed to "Press Only". "Stain Removal" removed.
const LINK_CONTENT = {

  // ── SERVICES ──────────────────────────────────────────────
  "Dry Cleaning": {
    title: "Dry Cleaning", icon: "🧥", color: "indigo",
    body: "Our dry cleaning service uses professional-grade solvents to gently remove dirt, oils and stains from delicate and formal garments. Suits, dresses, coats, silk and wool items are all handled with expert care. Every item is inspected, treated, cleaned and pressed before being returned to you in perfect condition.",
    points: ["Suits & formal wear","Silk & delicate fabrics","Coats & jackets","Wedding dresses","Uniforms"],
  },
  "Wash & Fold": {
    title: "Wash & Fold", icon: "👕", color: "blue",
    body: "Our wash and fold service handles your everyday laundry from start to finish. We sort, wash at the correct temperature, dry and neatly fold every item. Perfect for families, busy professionals and anyone who wants clean clothes without the hassle.",
    points: ["Everyday clothing","T-shirts & jeans","Underwear & socks","Bed sheets & towels","Children's clothing"],
  },
  "Alterations": {
    title: "Alterations", icon: "🪡", color: "purple",
    body: "Our expert tailors handle all types of alterations — hems, repairs, resizing, and more. We make your clothes fit the way they should. Bring any garment that does not quite fit right and we will transform it.",
    points: ["Hem adjustments","Waist & seat resizing","Sleeve shortening","Zipper replacement","General repairs"],
  },
  "Press Only": {                                      // ← renamed from "Iron Only"
    title: "Press Only", icon: "👔", color: "green",
    body: "Already clean but wrinkled? Our pressing service delivers a crisp, sharp press on every garment. Using professional steam presses at precise temperatures, we remove even the most stubborn creases. Perfect before interviews, meetings or special occasions.",
    points: ["Shirts & blouses","Trousers & skirts","Dresses","School uniforms","Office wear"],
  },
  "Duvet Wash": {
    title: "Duvet Wash", icon: "🛏️", color: "purple",
    body: "Our commercial-grade machines deep clean duvets, comforters and heavy bedding that home machines simply cannot handle properly. We wash at the correct temperature, dry completely and restore the loft of the filling — leaving your duvet fresh, fluffy and allergen-free.",
    points: ["Single & double duvets","King & super king","Down & synthetic fill","Comforters","Mattress toppers"],
  },

  // ── COMPANY ───────────────────────────────────────────────
  "About Us": {
    title: "About 1464 Country Cleaners", icon: "🏢", color: "indigo",
    body: "1464 Country Cleaners is Houston's most trusted professional laundry and dry cleaning service. We have built our reputation on three things — quality, reliability and genuine care for every garment we handle. From a single shirt to an entire wardrobe, every item receives the same expert attention.",
    points: ["Serving Houston since our founding","Over 2,000 happy customers","4.9 star Google rating","Fully insured and licensed","Free pickup & delivery — 25pc minimum"],
  },
  "Our Team": {
    title: "Our Team", icon: "👥", color: "blue",
    body: "Our team of cleaning professionals brings years of experience and genuine passion to every garment. From our front-of-house customer service team to our specialist dry cleaners and pressing experts — everyone at Country Cleaners is trained to the highest standard and committed to delivering results you will love.",
    points: ["Certified dry cleaning specialists","Expert pressing team","Professional tailors & alterations","Dedicated customer service","Friendly pickup & delivery drivers"],
  },
  "Blog": {
    title: "Garment Care Tips & Advice", icon: "📝", color: "green",
    body: "Our blog is where we share practical garment care advice, fabric tips and insider knowledge from our cleaning experts. Learn how to extend the life of your clothes, what to do when accidents happen and why professional cleaning makes such a difference.",
    points: ["How to remove common stains at home","When to dry clean vs machine wash","How to store seasonal clothing","Caring for delicate fabrics","Why professional pressing lasts longer"],
  },
  "Careers": {
    title: "Join Our Team", icon: "💼", color: "purple",
    body: "We are always looking for passionate, hardworking people to join the Country Cleaners family. Whether you are experienced in garment care or looking to learn a new skill, we provide full training, a supportive team environment and genuine opportunities to grow.",
    points: ["Dry cleaning technicians","Customer service representatives","Pickup & delivery drivers","Pressing specialists","Store supervisors"],
  },
  "FAQs": {
    title: "Frequently Asked Questions", icon: "❓", color: "indigo",
    body: "Here are the answers to the questions we hear most often from our customers.",
    faqs: [
      { q: "How does free pickup work?",              a: "Book online or call us. We collect your clothes from your door — completely free with a minimum of 25 garments." },
      { q: "What is the 25 piece minimum?",          a: "Free pickup and delivery to your door requires a minimum of 25 garments per order. This ensures we can provide the best care and service for every customer." },
      { q: "Do you service my area?",                a: "We currently serve customers within 5–10 miles of our location. Call us at (281) 277-0001 and we will confirm in seconds." },
      { q: "How long does cleaning take?",           a: "Most services are ready within 24–48 hours. We always confirm your collection time when we pick up." },
      { q: "What if I am not happy with the result?", a: "We redo it free of charge. Your satisfaction is 100% guaranteed — no questions asked." },
      { q: "Do you handle delicate or designer items?", a: "Absolutely. Our specialists are trained to handle all fabric types including silk, wool, cashmere and designer garments." },
      { q: "How do I pay?",                          a: "We accept all major cards, cash and online payment at the time of delivery." },
    ],
  },

  // ── LEGAL ─────────────────────────────────────────────────
  "Privacy Policy": {
    title: "Privacy Policy", icon: "🔒", color: "gray",
    body: "1464 Country Cleaners is committed to protecting your personal information. We collect only the information necessary to provide our service — your name, contact details and service preferences. We never sell your data to third parties.",
    points: ["We collect only what is necessary","Your data is never sold to third parties","All data is stored securely","You can request deletion of your data at any time","We use industry-standard encryption"],
  },
  "Terms of Service": {
    title: "Terms of Service", icon: "📋", color: "gray",
    body: "By using our services you agree to our terms. We handle all garments with professional care but cannot be held responsible for pre-existing damage, items left in pockets or garments that are not colourfast. Our liability is limited to the cost of the service provided.",
    points: ["All garments handled with professional care","Customers must declare pre-existing damage","Liability limited to service cost","Items left in pockets are not our responsibility","We reserve the right to refuse items we cannot safely clean"],
  },
  "Refund Policy": {
    title: "Refund Policy", icon: "💳", color: "green",
    body: "Your satisfaction is our priority. If you are not completely happy with your cleaned garment we will redo it free of charge. If after a second clean you are still not satisfied we will provide a full refund for that item. Refund requests must be made within 48 hours of delivery.",
    points: ["100% satisfaction guarantee","Free redo if not satisfied","Full refund if second clean unsatisfactory","Refund requests within 48 hours of delivery","No questions asked policy"],
  },
  "Cookie Policy": {
    title: "Cookie Policy", icon: "🍪", color: "gray",
    body: "Our website uses cookies to improve your browsing experience. We use essential cookies to make the website work and optional analytics cookies to understand how visitors use our site. We do not use advertising cookies or share cookie data with third parties.",
    points: ["Essential cookies for site function","Optional analytics cookies only","No advertising cookies","No third party cookie sharing","Manage preferences at any time"],
  },
};

const COLOR_MAP = {
  indigo: { bg:"bg-indigo-50",  header:"bg-indigo-600",  badge:"bg-indigo-100 text-indigo-700"  },
  blue:   { bg:"bg-blue-50",    header:"bg-blue-600",    badge:"bg-blue-100 text-blue-700"      },
  green:  { bg:"bg-green-50",   header:"bg-green-600",   badge:"bg-green-100 text-green-700"    },
  red:    { bg:"bg-red-50",     header:"bg-red-600",     badge:"bg-red-100 text-red-700"        },
  purple: { bg:"bg-purple-50",  header:"bg-purple-600",  badge:"bg-purple-100 text-purple-700"  },
  gray:   { bg:"bg-gray-50",    header:"bg-gray-700",    badge:"bg-gray-100 text-gray-700"      },
};

export default function Footer({ setBookOpen, scrollTo }) {
  const [modal, setModal] = useState(null);

  function openModal(linkName) {
    const content = LINK_CONTENT[linkName];
    if (content) setModal({ name: linkName, ...content });
  }

  function handleLinkClick(title, link) {
    if (title === "Services") { scrollTo("services"); }
    else { openModal(link); }
  }

  return (
    <>
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-4 gap-8 text-sm">

          {/* Brand column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {businessConfig.logoIsImage ? (
                <img src={businessConfig.logo} alt={businessConfig.name} className="h-24 w-auto object-contain"/>
              ) : (
                <div className="w-14 h-14 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                  {businessConfig.logo}
                </div>
              )}
            </div>
            <p className="text-gray-200 leading-relaxed text-sm mb-4 font-normal">{businessConfig.description}</p>
            <div className="flex gap-3">
              {Object.entries(businessConfig.social)
                .filter(([, url]) => url)
                .map(([platform, url]) => (
                <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-indigo-600 transition-colors capitalize text-xs font-bold">
                  {platform[0].toUpperCase()}
                </a>
              ))}
            </div>
          </div>

          {/* Footer link columns — driven from businessConfig.footerLinks */}
          {Object.entries(businessConfig.footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-xs">{title}</h4>
              <ul className="space-y-2.5">
                {links.map(l => (
                  <li key={l}>
                    <button onClick={() => handleLinkClick(title, l)}
                      className="text-gray-400 hover:text-indigo-400 transition-colors text-sm text-left flex items-center gap-1.5 group">
                      <span className="w-1 h-1 rounded-full bg-gray-600 group-hover:bg-indigo-400 transition-colors shrink-0"/>
                      {l}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-4 mt-10 pt-6 border-t border-gray-800 flex flex-col md:flex-row justify-between text-xs gap-2 text-gray-300">
          <p>© {new Date().getFullYear()} {businessConfig.name}. All rights reserved.</p>
          <p>Made by <span className="text-indigo-400 font-bold">MOI Kreatives</span> — TX, USA</p>
        </div>
      </footer>

      {/* ══ LINK MODAL ════════════════════════════════════════ */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className={`${COLOR_MAP[modal.color]?.header || "bg-indigo-600"} p-5 rounded-t-2xl flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{modal.icon}</span>
                <h3 className="text-white font-black text-lg">{modal.title}</h3>
              </div>
              <button onClick={() => setModal(null)} className="text-white/70 hover:text-white transition-colors p-1">
                <X size={20}/>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <p className="text-gray-600 text-sm leading-relaxed">{modal.body}</p>
              {modal.points && (
                <div className={`${COLOR_MAP[modal.color]?.bg || "bg-indigo-50"} rounded-2xl p-4 space-y-2`}>
                  {modal.points.map((p, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                      <CheckCircle size={14} className="text-green-500 shrink-0"/>{p}
                    </div>
                  ))}
                </div>
              )}
              {modal.faqs && (
                <div className="space-y-3">
                  {modal.faqs.map((faq, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4">
                      <p className="font-bold text-sm text-gray-900 mb-1">Q: {faq.q}</p>
                      <p className="text-gray-600 text-sm leading-relaxed">A: {faq.a}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setModal(null); setBookOpen(true); }}
                  className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black text-sm hover:bg-indigo-700 transition-colors">
                  📅 Book a Pickup
                </button>
                <button onClick={() => setModal(null)}
                  className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}